import { useEffect } from 'react';

/**
 * Pulls a page index back into range after the list behind it shrinks.
 *
 * The awards-service lists fix pageSize server-side, so when a status change or
 * filter drops the total below the current page's first row, that page returns
 * empty and the table dead-ends on "nothing here" with the stepper gone. This
 * clamps the page state back to the last real page so the next fetch lands on
 * data instead.
 *
 * Guards on totalPages, never totalCount directly, so it stays correct at any
 * pageSize. A total of 0 clamps to page 1 (never page 0).
 *
 * Pass a nullish totalCount to mean "unknown" — a failed fetch has no total,
 * and clamping on its 0 fallback would silently reset the admin's page to 1
 * and refetch over the error state. Unknown totals never clamp.
 *
 * @param {number} page - Current 1-based page.
 * @param {number|null} totalCount - Total rows, or nullish while unknown.
 * @param {number} pageSize - Rows per page.
 * @param {(page: number) => void} setPage - Setter for the page state.
 */
const useClampedPage = (page, totalCount, pageSize, setPage) => {
  const totalPages = totalCount == null
    ? null
    : Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    if (totalPages != null && page > totalPages) setPage(totalPages);
  }, [page, totalPages, setPage]);
};

export default useClampedPage;
